<script setup lang="ts">
import { ReportingPeriodicity } from '@swipe4work/api-client-fetch'

import { z } from 'zod'
import UpdatePasswordDialog from '@/pages/settings/dialogs/UpdatePasswordDialog.vue'
import LanguageSwitcher from '@/components/settings/LanguageSwitcher.vue'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { Form } from '@primevue/forms'
import { useEmployerApiClient } from '@/modules/api-clients/api-clients'

definePage({
  name: '/settings/UserProfile',
  path: '/instellingen/gebruikersprofiel',
  meta: {
    layout: 'false',
  },
})

const t = useI18n()
const toast = useToast()
const dialog = useDynamicDialog()
const employerApi = useEmployerApiClient()

const authStore = useAuthStore()

const { employerProfile, isFirebaseAuth } = storeToRefs(authStore)

const reportingPeriodicityOptions = Object.keys(ReportingPeriodicity).map(key => ({
  label: t.t(`${key.toLocaleLowerCase()}`),
  value: key,
}))

async function openUpdatePasswordDialog() {
  dialog.open(UpdatePasswordDialog, {
    props: {
      dismissableMask: true,
      style: {
        width: 'fit-content',
        height: 'auto',
      },
    },
  })
}

async function onFormSubmit({ values, valid }: {
  values: any | undefined,
  valid: boolean
}) {
  if (!valid || !values) {
    toast.error(t.t('settings.userProfile.validation.formValidationErrors'))

    return
  }

  employerApi.updateProfile(values)
    .then(async () => {
      toast.success(t.t('settings.changesSaved'))
      await authStore.fetchEmployerProfile()
    })
    .catch(error => {
      toast.error(t.t('settings.userProfile.validation.profileUpdateFailed'))
      throw error
    })
}

const resolver = zodResolver(
  z.object({
    firstName: z.string({
      required_error: t.t('settings.userProfile.validation.firstNameRequired'),
    }),
    lastName: z.string({
      required_error: t.t('settings.userProfile.validation.lastNameRequired'),
    }),
    reportingPeriodicity: z.nativeEnum(ReportingPeriodicity),
  }).required({
    firstName: true,
    lastName: true,
    reportingPeriodicity: true,
  }),
)
</script>

<template>
  <TabContentCard :title="$t('settings.userProfile.title', { firstName: employerProfile?.firstName, lastName: employerProfile?.lastName })" :subtitle="$t('settings.userProfile.subtitle')" class="p-2">
    <Form
      :resolver
      @submit="onFormSubmit"
      class="max-w-2xl w-full py-2 space-y-3"
      :initial-values="employerProfile"
    >
      <div class="grid grid-cols-2 gap-x-2">
        <FormField
          v-slot="$field"
          name="firstName"
          class="flex flex-col gap-1"
        >
          <div>
            <IftaLabel>
              <InputText class="w-full" />
              <label for="firstName">{{ $t('settings.userProfile.firstName') }}</label>
            </IftaLabel>

            <Message
              v-if="$field?.invalid"
              severity="error"
              size="small"
              variant="simple"
            >
              {{ $field.error?.message }}
            </Message>
          </div>
        </FormField>

        <FormField
          v-slot="$field"
          name="lastName"
          class="flex flex-col gap-1"
        >
          <IftaLabel>
            <InputText class="w-full" />
            <label for="lastName">{{ $t('settings.userProfile.lastName') }}</label>
          </IftaLabel>
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $field.error?.message }}
          </Message>
        </FormField>
      </div>

      <IftaLabel>
        <InputText class="w-full" :value="employerProfile?.email" :disabled="true" />
        <label for="email">{{ $t('settings.userProfile.email') }}</label>
      </IftaLabel>

      <FormField
        v-slot="$field"
        name="reportingPeriodicity"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <Select
            class="w-full"
            :options="reportingPeriodicityOptions"
            option-value="value"
            option-label="label"
          />
          <label for="reportingPeriodicity">{{ $t('settings.userProfile.reportingPeriodicity') }}</label>
        </IftaLabel>
        <small>
          {{ $t('settings.userProfile.reportingPeriodicityDescription') }}
        </small>
        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <div class="flex gap-x-2 pt-2">
        <Button type="submit" :label="$t('save')" />
        <FeatureButton outlined :label="$t('settings.userProfile.changePassword')" :action="openUpdatePasswordDialog" :permission="isFirebaseAuth" :disabled-tooltip-overwrite="$t('settings.userProfile.changePasswordDisabledTooltip')" />
      </div>
    </Form>
  </TabContentCard>

  <TabContentCard :title="$t('settings.language.title')" :subtitle="$t('settings.language.description')" class="mt-4 p-2">
    <div class="max-w-2xl w-full py-2">
      <LanguageSwitcher />
    </div>
  </TabContentCard>
</template>
